import { useState, type CSSProperties, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { api } from '../api';
import { useAuth } from '../auth';
import { GoogleSignInButton } from '../components/GoogleSignInButton';
import { TurnstileWidget } from '../components/TurnstileWidget';
import { useClientConfig } from '../lib/useClientConfig';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { safeReturnTo, withReturnTo } from '../lib/returnTo';

type Status = 'idle' | 'submitting' | 'error';

interface LocationState {
  from?: string;
  returnTo?: string;
  /** The address an invite was sent to (TOM-458): the form starts with it. */
  email?: string;
}

// Inline link-style button — there's no shared CSS class for in-form text
// actions, and the retry affordances live inside error banners.
const linkButtonStyle: CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  color: 'inherit',
  font: 'inherit',
  textDecoration: 'underline',
  cursor: 'pointer',
};

export function Signup() {
  useDocumentTitle('Create account - Neomaris');
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // No public page links here while in beta (TOM-888, ADR 0435). The
  // invite page's "Create an account" plumbs `from` through state; after
  // signup we auto-login and bounce back to that destination.
  const from = (location.state as LocationState | null)?.from ?? '/dashboard';
  // activation_return_link: carry a boat return_to in router state through
  // signup + auto-login, guarded to http(s).
  const returnTo = safeReturnTo((location.state as LocationState | null)?.returnTo);
  const dest = withReturnTo(from, returnTo);
  const [email, setEmail] = useState((location.state as LocationState | null)?.email ?? '');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);

  // Cloudflare Turnstile (threat E1). The site key arrives at runtime from
  // GET /v1/config — it can't be baked into the single shared SPA build
  // (ADR 0030). When it's null (local dev / test), no widget renders and
  // server-side verification is bypassed or fails open.
  const {
    config: clientConfig,
    loaded: configLoaded,
    error: configError,
    reload: reloadConfig,
  } = useClientConfig();
  const turnstileSiteKey = clientConfig?.turnstileSiteKey ?? null;
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  // Bumped to make the single-use widget issue a fresh token on retry.
  const [turnstileNonce, setTurnstileNonce] = useState(0);
  // Bumped to remount the widget (re-attempting the script load) after a
  // load failure.
  const [turnstileWidgetKey, setTurnstileWidgetKey] = useState(0);
  // The Turnstile script itself failed to load (blocker / CDN / offline).
  const [turnstileFailed, setTurnstileFailed] = useState(false);

  // Gate submit on (a) knowing whether Turnstile is required — i.e. the
  // /v1/config fetch settled successfully — and (b) holding a token when it
  // is. Submitting before config loads, or after a config-fetch failure,
  // would send a tokenless request the server rejects with `captcha_failed`
  // whenever a secret is configured (the very failure this flow fixes).
  // Google sign-in stays available regardless.
  const canSubmit =
    status !== 'submitting' &&
    configLoaded &&
    !configError &&
    (turnstileSiteKey === null || turnstileToken !== null);

  function retryTurnstile() {
    setTurnstileFailed(false);
    setTurnstileWidgetKey((k) => k + 1);
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    // Belt-and-suspenders against an Enter-key submit while the button is
    // disabled: never POST before config settles, nor without a token when
    // Turnstile is required.
    if (!configLoaded || configError) return;
    if (turnstileSiteKey !== null && turnstileToken === null) return;
    setStatus('submitting');
    setError(null);
    try {
      const body: { email: string; password: string; 'cf-turnstile-response'?: string } = {
        email,
        password,
      };
      if (turnstileToken !== null) body['cf-turnstile-response'] = turnstileToken;
      await api('/v1/auth/signup', { method: 'POST', body });
      // Auto-login on success — keeps the user on a single tab and
      // preserves the post-auth destination (e.g., /activate?token=…).
      // Email-verification gating (task p2_email) will reintroduce a
      // confirm-then-login step when it lands.
      try {
        await auth.login(email, password);
        navigate(dest, { replace: true });
      } catch {
        // Auto-login shouldn't really fail right after a successful
        // signup, but if it does (rate-limit, transient error)
        // surface a hand-off to the login form with the destination
        // preserved.
        navigate('/login', {
          replace: true,
          state: { from, ...(returnTo !== null ? { returnTo } : {}) },
        });
      }
    } catch (err) {
      const e = err as {
        status?: number;
        body?: { error?: string; message?: string };
        message?: string;
      };
      // ADR 0033: 409 email_in_use is the signal that an account
      // already exists. Hand off to /login with the matching banner
      // and the destination preserved so the user can sign in via
      // whichever method they originally used.
      if (e.status === 409 && e.body?.error === 'email_in_use') {
        navigate('/login?error=email_in_use', {
          replace: true,
          state: { from, ...(returnTo !== null ? { returnTo } : {}) },
        });
        return;
      }
      // The Turnstile token is single-use; this failed submit consumed it.
      // Reset the widget so the user's retry gets a fresh token.
      if (turnstileSiteKey !== null) {
        setTurnstileToken(null);
        setTurnstileNonce((n) => n + 1);
      }
      setStatus('error');
      // 4xx (e.g. password too short, invalid email) is real validation
      // we should show. 5xx falls through to a generic message.
      setError(e.body?.message ?? e.message ?? 'Something went wrong. Please try again.');
    }
  }

  return (
    <section className="auth-panel">
      <div className="auth-card auth-card-v2">
        <h2>Create account</h2>
        <p className="auth-sub">Set up your account and start watching over your boat.</p>
        <GoogleSignInButton from={from} returnTo={returnTo} />
        <div className="auth-divider" role="separator" aria-label="or">
          <span>or</span>
        </div>
        <form onSubmit={onSubmit} data-testid="signup-form">
          {error !== null ? (
            <div className="auth-banner auth-banner--error" role="alert">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path d="M12 9v4" strokeLinecap="round" />
                <path
                  d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"
                  strokeLinejoin="round"
                />
                <circle cx="12" cy="17" r="0.6" fill="currentColor" stroke="none" />
              </svg>
              <ul className="form-errors" data-testid="error-banner">
                <li>{error}</li>
              </ul>
            </div>
          ) : null}
          {configError ? (
            <div className="auth-banner auth-banner--error" role="alert">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path d="M12 9v4" strokeLinecap="round" />
                <path
                  d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"
                  strokeLinejoin="round"
                />
                <circle cx="12" cy="17" r="0.6" fill="currentColor" stroke="none" />
              </svg>
              <ul className="form-errors" data-testid="config-error-banner">
                <li>
                  We couldn’t load sign-up security.{' '}
                  <button type="button" style={linkButtonStyle} onClick={reloadConfig}>
                    Retry
                  </button>
                </li>
              </ul>
            </div>
          ) : null}
          <div className="form-group">
            <label htmlFor="signup-email">Email</label>
            <input
              type="email"
              id="signup-email"
              className="form-input"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <div className="field-password">
              <label htmlFor="signup-password">Password</label>
              <input
                type={showPw ? 'text' : 'password'}
                id="signup-password"
                className="form-input"
                aria-describedby="signup-password-req"
                autoComplete="new-password"
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button type="button" className="pw-toggle" onClick={() => setShowPw((v) => !v)}>
                <span className="sr-only">{showPw ? 'Hide password' : 'Show password'}</span>
                {showPw ? (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    aria-hidden="true"
                  >
                    <path d="M3 3l18 18" strokeLinecap="round" />
                    <path d="M10.6 10.6a2 2 0 0 0 2.8 2.8" strokeLinecap="round" />
                    <path
                      d="M9.4 5.2A9.6 9.6 0 0 1 12 5c5 0 9 4.5 9 7 0 1-1 2.6-2.7 4M6.3 6.3C3.9 7.8 3 10 3 12c0 1.4 1.9 4 5 5.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    aria-hidden="true"
                  >
                    <path
                      d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
            <div className="pw-hints">
              <span
                id="signup-password-req"
                className={`pw-hint ${password.length >= 8 ? 'pw-hint--met' : ''}`}
              >
                {password.length >= 8 ? (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    aria-hidden="true"
                  >
                    <path d="M5 12l4 4 10-10" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : null}
                At least 8 characters
              </span>
            </div>
          </div>
          {turnstileSiteKey !== null && !turnstileFailed ? (
            <div className="form-group">
              <TurnstileWidget
                key={turnstileWidgetKey}
                siteKey={turnstileSiteKey}
                onToken={setTurnstileToken}
                onExpire={() => setTurnstileToken(null)}
                onError={() => setTurnstileFailed(true)}
                resetSignal={turnstileNonce}
              />
            </div>
          ) : null}
          {turnstileFailed ? (
            <div className="auth-banner auth-banner--error" role="alert">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path d="M12 9v4" strokeLinecap="round" />
                <path
                  d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"
                  strokeLinejoin="round"
                />
                <circle cx="12" cy="17" r="0.6" fill="currentColor" stroke="none" />
              </svg>
              <ul className="form-errors" data-testid="turnstile-error-banner">
                <li>
                  We couldn’t finish a quick security check — usually a weak connection.{' '}
                  <button type="button" style={linkButtonStyle} onClick={retryTurnstile}>
                    Retry
                  </button>
                  , or try again in a moment.
                </li>
              </ul>
            </div>
          ) : null}
          <button type="submit" className="btn btn-primary auth-submit" disabled={!canSubmit}>
            {status === 'submitting' ? 'Creating…' : 'Create account'}
          </button>
        </form>
        <p className="auth-foot">
          Already have an account?{' '}
          <Link to="/login" state={{ from, ...(returnTo !== null ? { returnTo } : {}) }}>
            Sign in
          </Link>
        </p>
      </div>
    </section>
  );
}
